import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const isAndroid = () => Capacitor.getPlatform() === 'android';

/**
 * Android's back button walks back through the app's own history and only leaves the app from
 * its first screen, as on any native app. Does nothing in the browser.
 */
export function handleAndroidBackButton(goBack: () => void): () => void {
  if (!isAndroid()) return () => {};
  const listener = App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) goBack();
    else void App.exitApp();
  });
  return () => {
    void listener.then((handle) => handle.remove());
  };
}

/**
 * Opens a shared Libellus link (https://…/documents/…) at the right screen when Android hands
 * it to the app instead of the browser.
 */
export function handleAppLinks(open: (path: string) => void): () => void {
  if (!Capacitor.isNativePlatform()) return () => {};
  const listener = App.addListener('appUrlOpen', ({ url }) => {
    const { pathname, search } = new URL(url);
    open(`${pathname}${search}`);
  });
  return () => {
    void listener.then((handle) => handle.remove());
  };
}

/**
 * Opens another site's page: in a browser sheet over the app on Android, so the reader is still
 * in Libellus when they close it; in a new tab on the web.
 */
export async function openExternal(url: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await Browser.open({ url });
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled';

/** The system share sheet where there is one; otherwise the link is copied. */
export async function shareLink(link: {
  title: string;
  text?: string;
  url: string;
}): Promise<ShareOutcome> {
  try {
    if (Capacitor.isNativePlatform()) {
      await Share.share({
        title: link.title,
        text: link.text,
        url: link.url,
        dialogTitle: 'Share',
      });
      return 'shared';
    }
    if (navigator.share) {
      await navigator.share(link);
      return 'shared';
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
    if (error instanceof Error && /cancel/i.test(error.message)) return 'cancelled';
  }
  await navigator.clipboard.writeText(link.url);
  return 'copied';
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Saves a downloaded file. The browser downloads it; on Android it is written to the app's
 * cache and handed to the share sheet, from where it can be saved to Files or Drive or opened.
 */
export async function saveFile(blob: Blob, filename: string): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    const { uri } = await Filesystem.writeFile({
      path: filename,
      data: await toBase64(blob),
      directory: Directory.Cache,
    });
    await Share.share({ title: filename, files: [uri], dialogTitle: 'Save or open the PDF' });
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
